import { DialogContent } from "components/Dialog";
import { useCallback, useEffect, useState } from "react";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import { APIResponse, PageStatus } from "types";
import SuccessDialog from "components/SuccessDialog";
import moment from "moment";
import { studentToOrgTransactionTypeToHumanReadable } from "../../../Transactions";
import CurrencyInput, { CurrencyInputProps } from "react-currency-input-field";

import {
  ACHResponse,
  PaymentsService,
  CreditCardResponse,
  PaymentMethodType,
  StudentToOrgTransactionResponse,
  StudentLimitedResponse,
  StudentToOrgTransactionType,
} from "client/openapi";
import ErrorDialog from "components/ErrorDialog";
import Cards from "../../../../../../../../components/PaymentMethods/cards";
import Banks from "../../../../../../../../components/PaymentMethods/bank";

export default function RefundModal({
  transaction,
  student,
}: {
  transaction: StudentToOrgTransactionResponse;
  student: StudentLimitedResponse;
}) {
  const [status, setStatus] = useState<PageStatus>(PageStatus.EDITING);
  const [error, setError] = useState<APIResponse>();
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<
    CreditCardResponse | ACHResponse
  >();
  const [paymentEmail, setPaymentEmail] = useState<string>();
  const [isPartialRefund, setIsPartialRefund] = useState<boolean>(false);
  const [partialRefundAmount, setPartialRefundAmount] = useState<number | null>(
    null
  );
  const [invalidPartialAmount, setInvalidPartialAmount] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  type AllPaymentMethods = CreditCardResponse | ACHResponse;

  const [
    makeSureUserDoesntSpamRefundButton,
    setMakeSureUserDoesntSpamRefundButton,
  ] = useState<boolean>(false);

  const handleRefund = () => {
    PaymentsService.createRefund({
      transactionId: transaction.id,
      partialRefundAmount: isPartialRefund ? partialRefundAmount : null,
    })
      .then(() => {
        setStatus(PageStatus.SUCCESS);
      })
      .catch((err) => {
        setError(err);
        setStatus(PageStatus.ERROR);
      });

    setMakeSureUserDoesntSpamRefundButton(false);
  };

  const verifyPartialRefundAmount: CurrencyInputProps["onValueChange"] = (
    value,
    name
  ) => {
    // value is over limit
    if (Number(value) > transaction.amount / 100) {
      setInvalidPartialAmount(true);
      setError({
        message: `Please enter a value less than or equal to $${(
          transaction.amount / 100
        ).toFixed(2)}`,
      });
      return;
    }

    // value is under limit
    if (Number(value) <= 0) {
      setInvalidPartialAmount(true);
      setError({
        message: "Amount must be greater than $0.00!",
      });
      return;
    }

    // value is under limit
    if (
      Number(value) === null ||
      Number(value) === undefined ||
      Number.isNaN(Number(value))
    ) {
      setInvalidPartialAmount(true);
      setError({
        message: "Amount must be greater than $0.00!",
      });
      return;
    }

    setInvalidPartialAmount(false);
    setPartialRefundAmount(Number(value) * 100);
    setMakeSureUserDoesntSpamRefundButton(false);
  };

  const getAndSetPaymentMethodsOnFile = useCallback(async () => {
    setIsLoading(true);
    let newMethods: AllPaymentMethods[] = [];

    try {
      const existing_cards = await PaymentsService.getStudentCards({
        studentId: student.id,
      });
      if (existing_cards) {
        newMethods = [...newMethods, ...existing_cards];
      }
    } catch (error: any) {
      setError(error);
      setStatus(PageStatus.ERROR);
      console.error("Error:", error);
    }

    try {
      const existing_banks = await PaymentsService.getStudentAchAccounts({
        studentId: student.id,
      });
      if (existing_banks) {
        newMethods = [...newMethods, ...existing_banks];
      }
    } catch (error: any) {
      setError(error);
      setStatus(PageStatus.ERROR);
      console.error("Error:", error);
    }

    if (transaction.payment_method_type === PaymentMethodType.INVOICE) {
      setPaymentEmail(transaction.invoice_email as string);
    } else {
      setCurrentPaymentMethod(
        Array.from(newMethods).find(
          (paymentMethod) =>
            paymentMethod.payment_method_id === transaction.payment_method_id
        )
      );
    }
    setIsLoading(false);
  }, [
    student.id,
    transaction.invoice_email,
    transaction.payment_method_id,
    transaction.payment_method_type,
  ]);

  useEffect(() => {
    getAndSetPaymentMethodsOnFile();
  }, [getAndSetPaymentMethodsOnFile]);

  const reset = () => {
    getAndSetPaymentMethodsOnFile();
    setIsPartialRefund(false);
    setPartialRefundAmount(null);
    setInvalidPartialAmount(false);
  };

  if (status === PageStatus.SUCCESS) {
    return (
      <SuccessDialog
        message="Attempting refund. Reload the page to see your changes."
        onClose={() => {
          reset();
          setStatus(PageStatus.EDITING);
        }}
      />
    );
  }

  if (status === PageStatus.ERROR) {
    return (
      <ErrorDialog
        message="A Refund cannot be processed at this time"
        onClose={() => {
          reset();
          setStatus(PageStatus.EDITING);
        }}
      />
    );
  }

  return (
    <DialogContent className="dialog-content max-w-[800px]" onClose={reset}>
      <div className="mt-3 font-semibold text-center header text-lg">
        You are about to refund this transaction
      </div>

      <div className="mt-4">
        <p className="underline text-gray-300 header text-lg font-bold">Date</p>
        <p className="header text-sm">
          {moment(transaction.timestamp).format("MM/DD/YYYY")}
        </p>
      </div>

      <div className="mt-4">
        <p className="underline text-gray-300 header text-lg font-bold">
          Amount
        </p>
        <p className="header text-sm">
          ${(transaction.amount / 100).toFixed(2)}
        </p>
      </div>

      <div className="mt-4">
        <p className="underline text-gray-300 header text-lg font-bold">
          Billing Type
        </p>
        <p className="header text-sm">
          {
            studentToOrgTransactionTypeToHumanReadable.find(
              (t) => t.value === transaction.student_to_org_transaction_type
            )?.label
          }
        </p>
      </div>

      <div className="mt-4">
        <p className="underline text-gray-300 header text-lg font-bold">
          Service
        </p>
        <p className="header text-sm">{transaction.reason}</p>
      </div>

      <div className="mt-4 border border-gray-300 border-2 rounded-lg p-4">
        <p className="mb-2 text-sm font-bold">Refunding to:</p>
        <div>
          {isLoading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            </div>
          ) : transaction.student_to_org_transaction_type ===
            StudentToOrgTransactionType.MEETING_PAID_WITH_CREDITS ? (
            <span className="text-sm font-bold">Credits</span>
          ) : currentPaymentMethod || paymentEmail ? (
            <div>
              {currentPaymentMethod && "brand" in currentPaymentMethod ? (
                <Cards
                  key={currentPaymentMethod.payment_method_id}
                  card={currentPaymentMethod as CreditCardResponse}
                />
              ) : currentPaymentMethod &&
                "bank_name" in currentPaymentMethod ? (
                <Banks
                  key={currentPaymentMethod.payment_method_id}
                  bank={currentPaymentMethod as ACHResponse}
                />
              ) : (
                <span className="text-sm font-bold">{paymentEmail}</span>
              )}
            </div>
          ) : (
            <div>
              <span className="font-semibold text-sm">
                Payment method cannot be found. Perhaps it was deleted?
              </span>
            </div>
          )}
        </div>
      </div>

      {!isPartialRefund ? (
        <div className="mt-2">
          <button
            onClick={() => setIsPartialRefund(true)}
            className="text-blue-600 hover:text-blue-800 text-sm underline"
          >
            Want to issue a partial refund?
          </button>
        </div>
      ) : (
        <div>
          <div className="mt-4">
            <p className="text-sm font-bold">Partial Refund Amount</p>
            <CurrencyInput
              className="input w-11/12 h-3/6"
              id="partialRefundAmount"
              name="partialRefundAmount"
              placeholder={`$${(transaction.amount / 100).toFixed(2)}`}
              prefix={"$"}
              decimalsLimit={2}
              allowNegativeValue={false}
              onValueChange={verifyPartialRefundAmount}
            />
          </div>
          <div className="mt-2">
            <button
              onClick={() => {
                setIsPartialRefund(false);
                setPartialRefundAmount(null);
                setInvalidPartialAmount(false);
              }}
              className="text-blue-600 hover:text-blue-800 text-sm underline"
            >
              Want to issue a full refund?
            </button>
          </div>
        </div>
      )}

      <div className="w-full text-center mt-6">
        <Button
          color={
            makeSureUserDoesntSpamRefundButton ||
            transaction.refunded ||
            (isPartialRefund && invalidPartialAmount)
              ? ButtonColor.GRAY
              : ButtonColor.GREEN
          }
          disabled={
            makeSureUserDoesntSpamRefundButton ||
            transaction.refunded ||
            (isPartialRefund && invalidPartialAmount)
          }
          size={ButtonSize.SMALL}
          onClick={() => {
            handleRefund();
            setMakeSureUserDoesntSpamRefundButton(true);
          }}
        >
          Refund
        </Button>
      </div>
    </DialogContent>
  );
}
